import type { LiveCounts } from "@attendik/types/admin-schemas";

/**
 * Shared stage-capacity utilisation helpers (admin CR-01 §3.5). Lifted out of
 * the event-detail Overview tab so the dashboard's per-stage mini-strip uses
 * the exact same amber@80% / red@100% colour bands. Pure + presentational —
 * no data fetching.
 */

export type Tone = "neutral" | "ok" | "amber" | "red";

export const TONE_CLASSES: Record<Tone, string> = {
  // No capacity set, or below 80% — keep the default card look.
  neutral: "border-border bg-background text-foreground",
  ok: "border-border bg-background text-foreground",
  // Approaching capacity (>= 80%) — amber band so an operator notices.
  amber: "border-amber-300 bg-amber-50 text-amber-900",
  // At or over capacity (>= 100%) — red band; backend has already issued a
  // STAGE_FULL warning to the operator at the door.
  red: "border-red-400 bg-red-50 text-red-900"
};

export function utilisationTone(
  currentlyIn: number,
  capacity: number | null
): Tone {
  if (capacity == null || capacity === 0) {
    return "neutral";
  }
  const ratio = currentlyIn / capacity;
  if (ratio >= 1) {
    return "red";
  }
  if (ratio >= 0.8) {
    return "amber";
  }
  return "ok";
}

/**
 * Per-stage strip — one chip per stage showing `currently_in / capacity` with
 * the utilisation colour band. Stages without a capacity show just the count.
 */
export function PerStageStrip({
  perStage
}: {
  perStage: LiveCounts["per_stage"];
}): JSX.Element {
  return (
    <div className="rounded-lg border border-border bg-card p-3">
      <p className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
        Stages
      </p>
      <div className="flex flex-wrap gap-2" data-testid="per-stage-strip">
        {perStage.map((entry) => {
          const tone = utilisationTone(entry.currently_in, entry.capacity);
          return (
            <span
              key={entry.stage_id}
              data-utilisation={tone}
              className={`inline-flex items-center gap-2 rounded-md border px-3 py-1.5 text-sm ${TONE_CLASSES[tone]}`}
            >
              <span className="font-medium">{entry.stage_name}</span>
              <span>
                {entry.currently_in}
                {entry.capacity != null ? ` / ${entry.capacity}` : ""}
              </span>
            </span>
          );
        })}
      </div>
    </div>
  );
}
